/**
 * ErrorBoundary
 *
 * Catches unhandled render errors in any child component tree.
 * Shows a friendly recovery screen instead of a blank white page.
 *
 * Must be a class component — React's getDerivedStateFromError only
 * works with class-based lifecycle methods.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 */

import React from 'react';

interface Props   { children: React.ReactNode }
interface State   { hasError: boolean; error: Error | null }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // In production you'd forward this to Sentry / Datadog here
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = import.meta.env.DEV;

    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">

          {/* Icon */}
          <div className="text-6xl mb-6 select-none">💌</div>

          {/* Heading */}
          <h1 className="font-heading text-2xl text-navy mb-3">
            Something went wrong
          </h1>

          {/* Body */}
          <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
            An unexpected error occurred. Your data is safe, please refresh
            the page to continue, or{' '}
            <a
              href="mailto:hello@momentsent.com"
              className="text-sage hover:underline"
            >
              contact support
            </a>{' '}
            if the problem persists.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={this.handleReload} className="btn-primary">
              Refresh page
            </button>
            <button
              onClick={this.handleReset}
              className="btn-secondary"
            >
              Try again
            </button>
          </div>

          {/* Dev-only: show the raw error */}
          {isDev && this.state.error && (
            <details className="mt-8 text-left">
              <summary className="font-body text-xs text-gray-400 cursor-pointer mb-2">
                Error details (dev only)
              </summary>
              <pre className="text-xs text-red-500 bg-white border border-red-100 p-4 rounded-xl overflow-auto max-h-56 whitespace-pre-wrap break-words">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}

        </div>
      </div>
    );
  }
}
